import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";

// A single part's outcome from a bulk relist attempt. `reason` is only present on
// unsuccessful outcomes - mirrors GPMetals.Core.Models.BulkList.BulkRelistOutcome.
export interface BulkRelistOutcome {
  partId: string;
  tagNumber?: string;
  title?: string;
  vrm?: string;
  reason?: string;
}

export interface BulkRelistResultData {
  successful: BulkRelistOutcome[];
  unsuccessful: BulkRelistOutcome[];
}

export interface BulkRelistResultModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  result: BulkRelistResultData | null;
}

type TabKey = "unsuccessful" | "successful";

function OutcomeTable({
  outcomes,
  showReason,
}: {
  outcomes: BulkRelistOutcome[];
  showReason: boolean;
}) {
  if (outcomes.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-500">Nothing to show.</p>
    );
  }

  return (
    <div className="max-h-96 overflow-y-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky top-0 bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Tag Number
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              VRM
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Title
            </th>
            {showReason && (
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                Reason
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {outcomes.map((o, i) => (
            <tr key={`${o.partId}-${i}`}>
              <td className="px-3 py-2 text-sm text-gray-700">{o.tagNumber || "—"}</td>
              <td className="px-3 py-2 text-sm font-medium text-gray-900">
                {o.vrm || "—"}
              </td>
              <td className="px-3 py-2 text-sm text-gray-700">{o.title || "—"}</td>
              {showReason && (
                <td className="px-3 py-2 text-sm text-red-700">{o.reason || "—"}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Shows the outcome of a bulk relist action - which parts were successfully withdrawn
// and queued for relisting, and which were skipped along with the reason why, instead of
// the previous single generic toast that gave no visibility into partial failures.
export default function BulkRelistResultModal({
  open,
  setOpen,
  result,
}: BulkRelistResultModalProps) {
  const unsuccessful = result?.unsuccessful ?? [];
  const successful = result?.successful ?? [];

  // Default to the tab most likely to need attention.
  const [activeTab, setActiveTab] = useState<TabKey>("unsuccessful");

  // A new result arrives after mount, so pick the sensible default each time.
  useEffect(() => {
    if (result) {
      setActiveTab(unsuccessful.length > 0 ? "unsuccessful" : "successful");
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabBtn = (key: TabKey, active: boolean) =>
    `flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium focus:outline-none ${
      active
        ? "border-primary-600 text-primary-600"
        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
    }`;

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      <div>
        <Dialog.Title
          as="h3"
          className="text-lg font-medium leading-6 text-gray-900"
        >
          Bulk Relist Results
        </Dialog.Title>

        <div className="mt-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-2" aria-label="Tabs">
            <button
              type="button"
              className={tabBtn("unsuccessful", activeTab === "unsuccessful")}
              onClick={() => setActiveTab("unsuccessful")}
            >
              <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
              Unsuccessful ({unsuccessful.length})
            </button>
            <button
              type="button"
              className={tabBtn("successful", activeTab === "successful")}
              onClick={() => setActiveTab("successful")}
            >
              <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
              Successful ({successful.length})
            </button>
          </nav>
        </div>

        <div className="mt-4">
          {activeTab === "unsuccessful" ? (
            <OutcomeTable outcomes={unsuccessful} showReason={true} />
          ) : (
            <OutcomeTable outcomes={successful} showReason={false} />
          )}
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
